import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

interface CodeOption {
  id: string;
  label: string;
  description?: string;
}

interface CodeDropdownProps {
  selectedOption: CodeOption;
  onSelect: (option: CodeOption) => void;
}

const CodeDropdown: React.FC<CodeDropdownProps> = ({
  selectedOption,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const codeOptions: CodeOption[] = [
    {
      id: "FBC-115-163",
      label: "FBC - Accessibility (Eighth Edition, 2023)",
      description: "Florida Building Code - Accessiblity",
    },
    {
      id: "NFPA",
      label: "NFPA 72 (2019)",
      description: "National Fire Alarm and Signaling Code",
    },
    {
      id: "Code_3",
      label: "Code 3",
      description: "Coming a bit later!",
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (option: CodeOption) => {
    onSelect(option);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="font-medium text-gray-900">
                {selectedOption.label}
              </div>
              {selectedOption.id === "Code_3" && (
                <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                  ⏳ Coming Soon
                </span>
              )}
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-gray-400 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          <ul className="py-1">
            {codeOptions.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  className={`w-full px-4 py-2 text-left focus:outline-none ${
                    option.id !== "Code_3"
                      ? "hover:bg-gray-50 focus:bg-gray-50 cursor-pointer"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                  onClick={() => option.id !== "Code_3" && handleSelect(option)}
                  disabled={option.id === "Code_3"}
                >
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-gray-900">
                      {option.label}
                    </div>
                    {option.id === "Code_3" && (
                      <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                        ⏳ Coming Soon
                      </span>
                    )}
                  </div>
                  {option.description && (
                    <div className="text-sm text-gray-500">
                      {option.description}
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CodeDropdown;
